import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { PDFSkeleton } from '../../Common/Skeletons';
import PdfOpenCard from './PdfOpenCard';
import './PdfJsPages.css';

/**
 * PdfJsPages: the PDF viewer on phones and tablets (PDFViewer picks it on a touch device).
 *
 * iOS Safari paints a PDF iframe as one clipped, unscrollable image and Android Chrome
 * paints nothing, so here pdf.js renders every page into a canvas inside the viewer:
 * fitted to the width, scrolling vertically, pinch to zoom. pdf.js is loaded on first use
 * (pdfjsLoader, dynamic import), and anything that fails shows PdfOpenCard instead.
 */

const PAGE_PADDING = 10; // px around and between pages (sides are 0 in the phone takeover)
const MAX_DEVICE_PIXEL_RATIO = 2;
const MAX_CANVAS_PIXELS = 4000000; // ~16 MB per canvas, so a big page cannot exhaust iPad memory
const NEAR_VIEWPORT_MARGIN = '100% 0px'; // render pages within one screen above or below
const LETTER_RATIO = 11 / 8.5; // placeholder page shape until page 1 is measured
const LOAD_STALL_MS = 30000; // no download progress for this long: show the card
const DESTROY_WAIT_MS = 1000; // a busy or wedged worker never acknowledges destroy(); stop it anyway

// Zoom: same range and double-tap step as the image viewer (1x to 3x, toggle 2x).
const MIN_ZOOM = 1; // fit to width
const MAX_ZOOM = 3;
const DOUBLE_TAP_ZOOM = 2;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP = 30;
const TAP_SLOP = 10;

const clampZoom = (zoom) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

// Scale for page.getViewport(): fills cssWidth at the screen's pixel density (capped at 2x),
// then shrinks so the canvas stays under MAX_CANVAS_PIXELS.
export const canvasScale = (pageWidth, pageHeight, cssWidth, devicePixelRatio) => {
  const density = Math.min(Math.max(devicePixelRatio || 1, 1), MAX_DEVICE_PIXEL_RATIO);
  let scale = (cssWidth * density) / pageWidth;
  const pixels = pageWidth * pageHeight * scale * scale;
  if (pixels > MAX_CANVAS_PIXELS) scale *= Math.sqrt(MAX_CANVAS_PIXELS / pixels);
  return scale;
};

// iOS keeps a canvas's memory until its size is zeroed, so free it explicitly.
const releaseCanvas = (canvas) => {
  canvas.width = 0;
  canvas.height = 0;
};

const clearHolder = (holder) => {
  if (!holder) return;
  Array.from(holder.querySelectorAll('canvas')).forEach((canvas) => {
    releaseCanvas(canvas);
    canvas.remove();
  });
};

// Drop a page's operator list and decoded images (pdf.js keeps them until cleanup()), as
// pdf.js's own viewer does when it evicts a page. Safe mid-render: pdf.js then cleans up
// as soon as the cancelled render stops.
const cleanupPage = (pageRef) => {
  const page = pageRef.current;
  pageRef.current = null;
  if (!page) return;
  try {
    page.cleanup();
  } catch {
    // the document is already closing; nothing left to free
  }
};

// One page. Draws while near the viewport; when it scrolls away, frees its canvas and its
// pdf.js page data.
const PdfPage = memo(({ doc, pageNumber, defaultRatio, near, renderWidth, onPainted, onFailed }) => {
  const [ratio, setRatio] = useState(null);
  const holderRef = useRef(null);
  const pageRef = useRef(null); // the pdf.js page, kept so it can be cleaned up

  useEffect(() => {
    const holder = holderRef.current;
    if (!near) {
      // The previous render (if any) was cancelled by its effect cleanup just before this.
      clearHolder(holder);
      cleanupPage(pageRef);
      return undefined;
    }
    if (!doc || !holder || renderWidth <= 0) return undefined;

    let cancelled = false;
    let task = null;
    const canvas = document.createElement('canvas');

    doc
      .getPage(pageNumber)
      .then((page) => {
        if (cancelled) return undefined;
        pageRef.current = page;
        const base = page.getViewport({ scale: 1 });
        setRatio(base.height / base.width);
        const scale = canvasScale(base.width, base.height, renderWidth, window.devicePixelRatio);
        const viewport = page.getViewport({ scale });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Canvas is unavailable');
        task = page.render({ canvasContext: context, viewport });
        return task.promise.then(() => {
          if (cancelled) return;
          // Swap in the new canvas only once it is drawn, so a zoom never flashes white.
          clearHolder(holder);
          holder.appendChild(canvas);
          onPainted(doc);
        });
      })
      .catch((err) => {
        if (cancelled || (err && err.name === 'RenderingCancelledException')) return;
        releaseCanvas(canvas); // the failed page stays an empty box; don't hold up to 16 MB for it
        onFailed(doc, err);
      });

    return () => {
      cancelled = true;
      if (task) task.cancel();
      if (canvas.parentNode !== holder) releaseCanvas(canvas);
    };
  }, [doc, pageNumber, near, renderWidth, onPainted, onFailed]);

  // Unmount (also a url change): runs after the render effect's cleanup cancelled any render.
  useEffect(() => {
    const holder = holderRef.current;
    return () => {
      clearHolder(holder);
      cleanupPage(pageRef);
    };
  }, []);

  return (
    <div
      className="pdfjs-page"
      data-page={pageNumber}
      aria-label={`Page ${pageNumber}`}
      style={{ paddingTop: `${(ratio || defaultRatio) * 100}%` }}
    >
      <div className="pdfjs-page-canvas" ref={holderRef} />
    </div>
  );
});

const PdfJsPages = ({ url, filename, fileSize, isDarkMode }) => {
  const [doc, setDoc] = useState(null);
  const [defaultRatio, setDefaultRatio] = useState(LETTER_RATIO);
  const [failed, setFailed] = useState(false);
  const [painted, setPainted] = useState(false);
  const [nearPages, setNearPages] = useState(() => new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [baseWidth, setBaseWidth] = useState(0);
  const [sidePadding, setSidePadding] = useState(2 * PAGE_PADDING);
  const docRef = useRef(null); // the live document; late callbacks from a closed one are ignored
  const paintedRef = useRef(false); // a page of the live document has painted
  const zoomRef = useRef(MIN_ZOOM); // live zoom during a pinch (state is committed at the end)
  const scrollerRef = useRef(null);
  const contentRef = useRef(null);

  // Load the document. On unmount or a new url: destroy it, then stop its worker.
  useEffect(() => {
    let cancelled = false;
    let task = null;
    let pdfWorker = null;
    let webWorker = null;
    let stallTimer = null;
    let onVisibilityChange = null;
    const stopStallClock = () => {
      clearTimeout(stallTimer);
      stallTimer = null;
      if (onVisibilityChange) document.removeEventListener('visibilitychange', onVisibilityChange);
      onVisibilityChange = null;
    };

    paintedRef.current = false;
    setDoc(null);
    setFailed(false);
    setPainted(false);
    setNearPages(new Set());
    setCurrentPage(1);
    setZoom(MIN_ZOOM);
    zoomRef.current = MIN_ZOOM;
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = 0;
      scrollerRef.current.scrollLeft = 0;
    }

    (async () => {
      const loader = await import('./pdfjsLoader');
      const pdfjs = await loader.loadPdfjs();
      if (cancelled) return;
      const worker = await loader.startPdfWorker();
      if (cancelled) {
        worker.terminate();
        return;
      }
      webWorker = worker;
      pdfWorker = new pdfjs.PDFWorker({ port: worker });
      // isEvalSupported: false is mandatory (CVE-2024-4367: a crafted font could run script).
      task = pdfjs.getDocument({ url, isEvalSupported: false, worker: pdfWorker });
      // A download that makes no progress for LOAD_STALL_MS gives up and shows the card,
      // rather than leaving the skeleton up until the browser's own network timeout.
      const loadingTask = task;
      const armStallTimer = () => {
        clearTimeout(stallTimer);
        stallTimer = setTimeout(() => {
          if (cancelled) return;
          // iOS pauses timers and the download while the app is in the background: a hidden
          // tab is not a stalled load, so give it another full period.
          if (document.visibilityState === 'hidden') {
            armStallTimer();
            return;
          }
          stopStallClock();
          cancelled = true; // a late load must not replace the card
          setFailed(true);
          loadingTask.destroy().catch(() => {});
        }, LOAD_STALL_MS);
      };
      // Back in the foreground: restart the clock rather than let an overdue timer fire before
      // the paused download has had a chance to report progress.
      onVisibilityChange = () => {
        if (stallTimer && document.visibilityState === 'visible') armStallTimer();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      task.onProgress = armStallTimer;
      armStallTimer();
      const loaded = await task.promise;
      const firstPage = await loaded.getPage(1);
      stopStallClock(); // loaded: from here on, page failures decide
      if (cancelled) return;
      const { width, height } = firstPage.getViewport({ scale: 1 });
      docRef.current = loaded;
      setDefaultRatio(height / width);
      setDoc(loaded);
    })().catch(() => {
      stopStallClock();
      if (!cancelled) setFailed(true);
    });

    return () => {
      cancelled = true;
      docRef.current = null;
      stopStallClock();
      // Let pdf.js close the document cleanly, but never wait on it for more than
      // DESTROY_WAIT_MS: the worker is terminated either way.
      const closing = task ? task.destroy().catch(() => {}) : Promise.resolve();
      let waitTimer = null;
      const waited = new Promise((resolve) => {
        waitTimer = setTimeout(resolve, DESTROY_WAIT_MS);
      });
      Promise.race([closing, waited]).then(() => {
        clearTimeout(waitTimer);
        if (pdfWorker) pdfWorker.destroy();
        if (webWorker) webWorker.terminate();
      });
    };
  }, [url]);

  const handlePainted = useCallback((pageDoc) => {
    if (pageDoc !== docRef.current) return;
    paintedRef.current = true;
    setPainted(true);
  }, []);

  // A page that fails before anything has painted means the PDF cannot be shown: the card.
  // Once a page has painted, a failed page just stays an empty white page box.
  const handleFailed = useCallback((pageDoc) => {
    if (pageDoc === docRef.current && !paintedRef.current) setFailed(true);
  }, []);

  // Fit-to-width: track the viewer's width (rotation, split panels).
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    const measure = () => {
      setBaseWidth(scroller.clientWidth);
      // Read the real side padding: 0 in the phone takeover (PdfJsPages.css), 10px + 10px elsewhere.
      const content = contentRef.current;
      if (content) {
        const style = window.getComputedStyle(content);
        const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        if (Number.isFinite(padding)) setSidePadding(padding);
      }
    };
    measure();
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(measure);
      observer.observe(scroller);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [failed]);

  // Lazy rendering: only pages near the viewport get a canvas.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const content = contentRef.current;
    if (!doc || failed || !scroller || !content) return undefined;
    const pages = Array.from(content.querySelectorAll('.pdfjs-page'));
    if (typeof IntersectionObserver !== 'function') {
      // Browsers older than iOS 12.2: no lazy rendering, draw every page.
      setNearPages(new Set(pages.map((_, i) => i + 1)));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        setNearPages((prev) => {
          const next = new Set(prev);
          entries.forEach((entry) => {
            const pageNumber = Number(entry.target.dataset.page);
            if (entry.isIntersecting) next.add(pageNumber);
            else next.delete(pageNumber);
          });
          return next;
        });
      },
      { root: scroller, rootMargin: NEAR_VIEWPORT_MARGIN }
    );
    pages.forEach((page) => observer.observe(page));
    return () => observer.disconnect();
  }, [doc, failed]);

  // "Page N of M": the last page whose top is above the middle of the viewer.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const content = contentRef.current;
    if (!doc || failed || !scroller || !content || doc.numPages < 2) return undefined;
    const pages = content.querySelectorAll('.pdfjs-page');
    let frame = 0;
    const update = () => {
      frame = 0;
      const { scrollTop, clientHeight, scrollHeight } = scroller;
      let current = 1;
      if (scrollTop <= 0) {
        current = 1; // at the very top, even if page 2 starts above the middle (short pages)
      } else if (scrollTop + clientHeight >= scrollHeight - 2) {
        current = pages.length; // at the very end, even if the last page is short
      } else {
        const middle = scrollTop + clientHeight / 2;
        let lo = 0;
        let hi = pages.length - 1;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if (pages[mid].offsetTop <= middle) {
            current = mid + 1;
            lo = mid + 1;
          } else {
            hi = mid - 1;
          }
        }
      }
      setCurrentPage(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [doc, failed]);

  // Pinch to zoom and double-tap, about the fingers. Zoom changes the pages' layout width
  // (not a CSS transform), so a zoomed page really scrolls sideways; the phone viewer's
  // swipe-to-next-file handler already ignores swipes that start in a sideways scroller.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const content = contentRef.current;
    if (!doc || failed || !scroller || !content) return undefined;

    let pinch = null; // two-finger gesture in progress
    let pinched = false; // this touch sequence included a pinch
    let tap = null; // one-finger touch that may still be a tap
    let lastTap = null;

    const pointIn = (touches) => {
      const rect = scroller.getBoundingClientRect();
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
        y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top,
      };
    };
    const spread = (touches) =>
      Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY) || 1;

    // Zoom keeping the content under (x, y), relative to the scroller, in place.
    const zoomAt = (next, x, y) => {
      const prev = zoomRef.current;
      if (next === prev) return;
      const contentX = scroller.scrollLeft + x;
      const contentY = scroller.scrollTop + y;
      zoomRef.current = next;
      content.style.width = `${next * 100}%`;
      scroller.scrollLeft = (contentX * next) / prev - x;
      scroller.scrollTop = (contentY * next) / prev - y;
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        pinch = { spread: spread(e.touches), zoom: zoomRef.current, ...pointIn(e.touches) };
        pinched = true;
        tap = null;
      } else if (e.touches.length === 1 && !pinched) {
        tap = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else {
        tap = null;
      }
    };

    const onTouchMove = (e) => {
      if (tap && e.touches.length === 1) {
        const moved = Math.hypot(e.touches[0].clientX - tap.x, e.touches[0].clientY - tap.y);
        if (moved > TAP_SLOP) tap = null;
      }
      if (!pinch || e.touches.length !== 2) return;
      e.preventDefault(); // the pinch zooms the pages, not the whole page
      const point = pointIn(e.touches);
      zoomAt(clampZoom(pinch.zoom * (spread(e.touches) / pinch.spread)), point.x, point.y);
      // Two fingers moving together also pan.
      scroller.scrollLeft -= point.x - pinch.x;
      scroller.scrollTop -= point.y - pinch.y;
      pinch.x = point.x;
      pinch.y = point.y;
    };

    const onTouchEnd = (e) => {
      if (pinched) {
        // A pinch is not a swipe: keep it from the viewer's swipe-to-next-file handler.
        e.stopPropagation();
        if (pinch && e.touches.length < 2) {
          pinch = null;
          setZoom(zoomRef.current);
        }
        if (e.touches.length === 0) pinched = false;
        return;
      }
      if (!tap || e.touches.length > 0 || e.type === 'touchcancel') {
        tap = null;
        return;
      }
      const now = Date.now();
      const isDoubleTap =
        lastTap &&
        now - lastTap.time < DOUBLE_TAP_MS &&
        Math.hypot(tap.x - lastTap.x, tap.y - lastTap.y) < DOUBLE_TAP_SLOP;
      if (isDoubleTap) {
        e.preventDefault(); // no synthetic click, no browser double-tap zoom
        const rect = scroller.getBoundingClientRect();
        const next = zoomRef.current > MIN_ZOOM ? MIN_ZOOM : DOUBLE_TAP_ZOOM;
        zoomAt(next, tap.x - rect.left, tap.y - rect.top);
        setZoom(next);
        lastTap = null;
      } else {
        lastTap = { x: tap.x, y: tap.y, time: now };
      }
      tap = null;
    };

    // Older iOS Safari zooms the whole page on a pinch unless its gesture event is cancelled.
    const onGestureStart = (e) => e.preventDefault();

    scroller.addEventListener('touchstart', onTouchStart, { passive: true });
    scroller.addEventListener('touchmove', onTouchMove, { passive: false });
    scroller.addEventListener('touchend', onTouchEnd, { passive: false });
    scroller.addEventListener('touchcancel', onTouchEnd, { passive: false });
    scroller.addEventListener('gesturestart', onGestureStart);
    return () => {
      scroller.removeEventListener('touchstart', onTouchStart);
      scroller.removeEventListener('touchmove', onTouchMove);
      scroller.removeEventListener('touchend', onTouchEnd);
      scroller.removeEventListener('touchcancel', onTouchEnd);
      scroller.removeEventListener('gesturestart', onGestureStart);
    };
  }, [doc, failed]);

  if (failed) {
    return <PdfOpenCard url={url} filename={filename} fileSize={fileSize} isDarkMode={isDarkMode} />;
  }

  const numPages = doc ? doc.numPages : 0;
  const renderWidth = Math.max(0, Math.round(baseWidth * zoom) - sidePadding);
  const pageNumbers = Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <div className={`pdf-viewer pdfjs-viewer ${isDarkMode ? 'dark' : 'light'}`}>
      {!painted && (
        <div className="pdf-viewer-loading">
          <PDFSkeleton />
        </div>
      )}
      <div ref={scrollerRef} className="pdfjs-viewer-scroller" style={{ opacity: painted ? 1 : 0 }}>
        <div key={url} ref={contentRef} className="pdfjs-viewer-pages" style={{ width: `${zoom * 100}%` }}>
          {pageNumbers.map((pageNumber) => (
            <PdfPage
              key={pageNumber}
              doc={doc}
              pageNumber={pageNumber}
              defaultRatio={defaultRatio}
              near={nearPages.has(pageNumber)}
              renderWidth={renderWidth}
              onPainted={handlePainted}
              onFailed={handleFailed}
            />
          ))}
        </div>
      </div>
      {painted && numPages > 1 && (
        <div className="pdfjs-viewer-pill">
          Page {currentPage} of {numPages}
        </div>
      )}
    </div>
  );
};

export default PdfJsPages;
